import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d67fbe7mvg9ih2c794u0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-[#0A0A0A] rounded-t-[40px] pt-20 pb-12 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-16">
          {/* Colonne Gauche - Newsletter */}
          <div className="md:col-span-5">
            <h3 className="text-5xl font-thin text-white mb-6 leading-tight">
              Restons<br />Connectés
            </h3>
            <p className="text-base text-gray-400 leading-relaxed mb-12">
              Abonnez-vous pour suivre mes projets et recevoir les dernières actualités sur mes réalisations.
            </p>

            <form id="newsletter-form" data-readdy-form onSubmit={handleNewsletterSubmit} className="space-y-6">
              <input
                type="email"
                name="email"
                placeholder="Votre adresse email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-transparent border-b border-white text-white placeholder-gray-500 py-3 focus:outline-none focus:border-[#0066FF] transition-colors text-sm"
              />

              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-white text-[#0A0A0A] px-8 py-4 rounded-full font-bold flex items-center gap-3 hover:bg-[#0066FF] hover:text-white transition-all duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Envoi...' : 'S\'abonner'}
                <div className="w-8 h-8 bg-[#0A0A0A] rounded-full flex items-center justify-center">
                  <i className="ri-arrow-right-line text-white"></i>
                </div>
              </button>

              {submitStatus === 'success' && (
                <div className="text-green-400 text-sm">Merci pour votre abonnement !</div>
              )}
              {submitStatus === 'error' && (
                <div className="text-red-400 text-sm">Une erreur est survenue. Veuillez réessayer.</div>
              )}
            </form>
          </div>

          {/* Colonne Milieu - Navigation */}
          <div className="md:col-span-3">
            <h4 className="text-sm text-gray-500 mb-6">Navigation</h4>
            <div className="space-y-4">
              <button
                onClick={() => scrollToSection('hero')}
                className="block text-base text-white hover:text-[#0066FF] transition-colors cursor-pointer underline"
              >
                {t('nav.home')}
              </button>
              <button
                onClick={() => scrollToSection('projects')}
                className="block text-base text-white hover:text-[#0066FF] transition-colors cursor-pointer underline"
              >
                {t('nav.projects')}
              </button>
              <button
                onClick={() => scrollToSection('blog')}
                className="block text-base text-white hover:text-[#0066FF] transition-colors cursor-pointer underline"
              >
                {t('nav.blog')}
              </button>
              <button
                onClick={() => scrollToSection('skills')}
                className="block text-base text-white hover:text-[#0066FF] transition-colors cursor-pointer underline"
              >
                {t('nav.skills')}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="block text-base text-white hover:text-[#0066FF] transition-colors cursor-pointer underline"
              >
                {t('nav.contact')}
              </button>
            </div>
          </div>

          {/* Colonne Droite - Contact */}
          <div className="md:col-span-4">
            <h4 className="text-sm text-gray-500 mb-6">Contact</h4>
            <div className="space-y-4 mb-10">
              <div className="text-base text-white">+221 76 617 82 84</div>
              <div className="text-base text-white">sadiocherif11@gmail.com</div>
            </div>

            <h4 className="text-sm text-gray-500 mb-4">Localisation</h4>
            <div className="text-base text-white leading-relaxed">
              Dakar,<br />Sénégal
            </div>
          </div>
        </div>

        {/* Barre Inférieure */}
        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Liens sociaux */}
            <div className="flex items-center gap-6">
              <a
                href="https://github.com/brahim1205"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-white hover:text-[#0066FF] transition-colors underline cursor-pointer"
              >
                GitHub
              </a>
              <span className="text-gray-600">•</span>
              <a
                href="https://linkedin.com/in/cherif-ibrahima-aidara-sadio-5ba9a8256"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-white hover:text-[#0066FF] transition-colors underline cursor-pointer"
              >
                LinkedIn
              </a>
            </div>

            {/* Copyright */}
            <div className="text-sm text-gray-500">
              © 2025 Cherif Ibrahima Aïdara Sadio
            </div>


          </div>
        </div>
      </div>
    </footer>
  );
}
